import cn from 'classnames';
import Button from 'components/button-new';
import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import ProductCardSkeleton from 'components/product-card/skeleton/index.';
import ProductImpressionsTracker from 'components/product-impressions-tracker';
import { useConfig } from 'config';
import { FeatureFlag, getFeatureFlag } from 'constants/feature-flag';
import { Entry } from 'contentful';
import { useSegmentationContext } from 'contexts/segmentation';
import { IProductPagesFields, ISectionAdsTile } from 'global';
import useMobile from 'hooks/common/use-mobile';
import useBestsellerStyleProperties from 'hooks/product/use-bestseller-style-properties';
import { possibleTags } from 'interfaces/collection-filter';
import { CollectionProduct } from 'models/collection/collection';
import { useCollectionState } from 'modules/collection/state';
import dynamic from 'next/dynamic';
import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { gtm, selectElement } from 'tracking';
import { isLegacyProductPageModel } from 'utils/contentful';
import AdTileWrapper from '../ad-tile-wrapper';
import AllergiesFilter from '../allergies-filter';
import FilterLaptop from '../collection-filter/laptop';
import FilterMobile from '../collection-filter/mobile';
import HeroFilterContainer from '../hero-filter/index.tsx';
import BestSellerWrapper from './best-seller-wrapper';

const ProductCard = dynamic(() => import('components/product-card'));

interface IProductsGridProps {
  bestsellerSectionClassName?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
  /** Contentful data connected with collection. if omitted, no adtiles would be shown */
  contentfulData?: Entry<IProductPagesFields> | null;
  customButton?: React.ReactNode;
  petProfileSelectorClassName?: string;
  productsLimit?: number;
  shouldShowAllergyFilter?: boolean;
  shouldShowBlackRibbon?: boolean;
  showFilter: boolean;
  showMoreButton?: boolean;
  trackingElementName?: string;
  /** If true, best seller ribbon is not shown */
  omitBestSeller?: boolean;
  setTemporarySelectedAllergies?: React.Dispatch<
    React.SetStateAction<possibleTags[] | undefined>
  >;
  /** Url query to be added  */
  additionalQuery?: Record<string, string>;
}

const TRACKING_NAME = 'Button Load More Products';

/**
 * This component is responsible for rendering the products grid of a collection.
 * It also renders the filter and the allergy filter.
 */
const ProductsGrid: React.FC<IProductsGridProps> = ({
  bestsellerSectionClassName,
  containerRef,
  contentfulData,
  customButton,
  omitBestSeller = false,
  productsLimit,
  shouldShowAllergyFilter,
  shouldShowBlackRibbon,
  showFilter,
  showMoreButton,
  trackingElementName,
  setTemporarySelectedAllergies,
  additionalQuery,
}) => {
  const { shopId } = useConfig();
  const featureFlg = getFeatureFlag(FeatureFlag.dynamicUxTest, shopId);

  const { multipleSegmentation, shouldOmitPersonalization } =
    useSegmentationContext();
  const isMobile = useMobile();
  const {
    isValidating,
    collection,
    limitOfProductsToShow,
    visibleProducts,
    setLimitOfProductsToShow,
    appliedFilter,
    filterConfig,
    activeAllergyFilter,
    resetFilter,
    toggleFilter,
    toggleActiveAllergyFilter,
    toggleAllergyFilter,
    trackingListName,
  } = useCollectionState();

  useEffect(() => {
    if (productsLimit) {
      setLimitOfProductsToShow(productsLimit);
    } else {
      setLimitOfProductsToShow(undefined);
    }
  }, [productsLimit, setLimitOfProductsToShow]);

  const ref = containerRef ?? createRef<HTMLDivElement>();

  const adsTiles = useMemo(() => {
    const adtiles: Array<{ index: number; value: ISectionAdsTile }> = [];
    if (contentfulData && isLegacyProductPageModel(contentfulData)) {
      contentfulData.fields.collectionsAdsTiles?.map((tile) => {
        adtiles.push({
          // subtract as position begins with 1
          index: tile.fields.position - 1,
          value: tile,
        });
      });
    }

    return adtiles;
  }, [contentfulData]);

  const visibleElements = useMemo(() => {
    const sortedAdtiles = [...adsTiles].sort((a, b) => a.index - b.index);
    const filteredBestsellers = visibleProducts.filter((product) => {
      return omitBestSeller || shouldOmitPersonalization
        ? product
        : !product.getIsBestSeller({
            segment: multipleSegmentation?.lastSegmentation,
          });
    });

    // Only when dynamic-ux is not available, we re-sort by availability
    if (!featureFlg) {
      filteredBestsellers.sort((a, b) => {
        if (a.availableForSale === b.availableForSale) return 0;
        return a.availableForSale ? -1 : 1; //
      });
    }

    const _visibleProducts: Array<
      ISectionAdsTile | CollectionProduct | JSX.Element
    > = [
      ...filteredBestsellers,
      // limit by limitOfProductsToShow. if limitOfProductsToShow is undefined, slice returns all the elements
    ].slice(0, limitOfProductsToShow);

    for (let i = adsTiles.length - 1; i >= 0; i--) {
      const { index, value } = sortedAdtiles[i];
      if (index < _visibleProducts.length) {
        _visibleProducts.splice(index, 0, value);
      }
    }

    return _visibleProducts;
  }, [
    omitBestSeller,
    adsTiles,
    featureFlg,
    limitOfProductsToShow,
    multipleSegmentation?.lastSegmentation,
    shouldOmitPersonalization,
    visibleProducts,
  ]);

  const bestsellerProducts = useMemo(() => {
    if (omitBestSeller) return [];
    return visibleProducts.filter((product) => {
      return (
        product.availableForSale &&
        product.getIsBestSeller({
          segment: multipleSegmentation?.lastSegmentation,
        })
      );
    });
  }, [multipleSegmentation?.lastSegmentation, omitBestSeller, visibleProducts]);

  const { paddingValue, shouldApplyPadding } = useBestsellerStyleProperties({
    bestsellerProductsLength: bestsellerProducts.length,
  });

  const moreProductsWithoutVisibleProducts = useMemo(() => {
    return collection.products
      .filter(
        (product) =>
          !visibleProducts.find(
            (visibleProduct) => visibleProduct.id === product.id
          )
      )
      .slice(0, 9);
  }, [collection, visibleProducts]);

  /**
   * Fake loading for test
   * @see https://petsdeli.atlassian.net/browse/PT-6960
   */
  const [fakeLoading, setFakeLoading] = useState<boolean>(false);
  const triggerFakeLoading = useCallback(() => {
    setFakeLoading(true);
    const id = setTimeout(() => {
      setFakeLoading(false);
    }, 1000);
    return () => clearTimeout(id);
  }, []);

  const isLoading = fakeLoading || isValidating;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-5" ref={ref}>
        {collection.products.length > 0 && showFilter ? (
          <>
            {/* laptop */}
            <div className="col-span-1 hidden lg:block">
              <div className="sticky top-0 ">
                <FilterLaptop
                  appliedFilter={appliedFilter}
                  filterConfig={filterConfig}
                  activeAllergyFilter={activeAllergyFilter}
                  resetFilter={resetFilter}
                  toggleFilter={toggleFilter}
                  collectionProducts={collection.products}
                  visibleProducts={visibleProducts}
                />
              </div>
            </div>
            {/* mobile */}
            <div className="lg:hidden">
              <FilterMobile
                appliedFilter={appliedFilter}
                filterConfig={filterConfig}
                activeAllergyFilter={activeAllergyFilter}
                resetFilter={resetFilter}
                toggleFilter={toggleFilter}
                collectionProducts={collection.products}
                visibleProducts={visibleProducts}
                sortCallback={triggerFakeLoading}
              />
              {shouldShowAllergyFilter && (
                <AllergiesFilter
                  activeAllergyFilter={activeAllergyFilter}
                  collectionProducts={collection.products}
                  setTemporarySelectedAllergies={setTemporarySelectedAllergies}
                  toggleActiveAllergyFilter={toggleActiveAllergyFilter}
                  toggleAllergyFilter={toggleAllergyFilter}
                  toggleFilter={toggleFilter}
                />
              )}
              <HeroFilterContainer
                className="mt-2"
                collectionProducts={collection.products}
                filterConfig={filterConfig}
              />
            </div>
          </>
        ) : null}
        <div
          className="col-span-1 lg:col-span-3"
          data-test="cdp-collection-tile-wrapper"
        >
          <HeroFilterContainer
            className="mb-3 hidden lg:flex"
            collectionProducts={collection.products}
            filterConfig={filterConfig}
          />
          {shouldShowAllergyFilter && (
            <div className="mb-[30px] hidden lg:block">
              <AllergiesFilter
                activeAllergyFilter={activeAllergyFilter}
                collectionProducts={collection.products}
                setTemporarySelectedAllergies={setTemporarySelectedAllergies}
                toggleActiveAllergyFilter={toggleActiveAllergyFilter}
                toggleAllergyFilter={toggleAllergyFilter}
                toggleFilter={toggleFilter}
              />
            </div>
          )}
          <div
            className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-5"
            data-test="cdp-collection-tile-wrapper"
          >
            {isLoading && (
              <>
                {new Array(9).fill(null).map((_, index) => (
                  <ProductCardSkeleton key={index} />
                ))}
              </>
            )}
            {!isLoading &&
              !shouldOmitPersonalization &&
              bestsellerProducts.length > 0 && (
                <BestSellerWrapper
                  bestSellerLength={bestsellerProducts.length}
                  message={collection.getBestSellerTitle({
                    segmentation: multipleSegmentation?.lastSegmentation,
                  })}
                  className={cn(bestsellerSectionClassName, 'lg:ml-0')}
                  isMobile={isMobile}
                >
                  {bestsellerProducts.map((product, index: number) => (
                    <ProductImpressionsTracker
                      element="Product List"
                      key={`${product.id}-${product.promoteVariantId}`}
                      list={collection.title}
                      position={index}
                      product={product}
                      trackingGroupName={trackingListName || ''}
                      collectionHandle={trackingListName}
                    >
                      <div
                        data-test="product-tile-container"
                        className="flex rounded-lg bg-other-white"
                      >
                        <ProductCard
                          collectionTitle={collection.title}
                          isBestSeller={true}
                          position={index}
                          product={product}
                          starSize={isMobile ? 'small' : 'medium'}
                          trackingElementName={trackingElementName}
                          additionalQuery={additionalQuery}
                          shouldShowBlackRibbon={shouldShowBlackRibbon}
                        />
                      </div>
                    </ProductImpressionsTracker>
                  ))}
                </BestSellerWrapper>
              )}

            {!isLoading &&
              visibleElements.map((element, index: number) => {
                if (element['handle']) {
                  const product = element as unknown as CollectionProduct;
                  const _shouldApplyPadding = shouldApplyPadding(index);

                  return (
                    <ProductImpressionsTracker
                      element={trackingElementName ?? 'Product List'}
                      key={`${product.id}-${product.promoteVariantId}`}
                      list={collection.title}
                      position={index}
                      product={product}
                      trackingGroupName={trackingListName || ''}
                      collectionHandle={trackingListName}
                    >
                      <div
                        data-test="product-tile-container"
                        className="flex"
                        style={{
                          paddingTop: _shouldApplyPadding
                            ? `${paddingValue}px`
                            : '',
                          paddingBottom: _shouldApplyPadding ? '8px' : '',
                        }}
                      >
                        <ProductCard
                          collectionTitle={collection.title}
                          isBestSeller={
                            shouldOmitPersonalization
                              ? false
                              : !omitBestSeller &&
                                product.getIsBestSeller({
                                  segment:
                                    multipleSegmentation?.lastSegmentation,
                                })
                          }
                          position={index}
                          product={product}
                          starSize={isMobile ? 'small' : 'medium'}
                          trackingElementName={trackingElementName}
                          additionalQuery={additionalQuery}
                          shouldShowBlackRibbon={shouldShowBlackRibbon}
                        />
                      </div>
                    </ProductImpressionsTracker>
                  );
                } else if (element['sys']) {
                  const adtile = element as unknown as ISectionAdsTile;
                  return (
                    <AdTileWrapper
                      entry={adtile}
                      key={adtile.sys.id}
                      title={collection.title}
                    />
                  );
                }
              })}
          </div>

          {!limitOfProductsToShow &&
            visibleProducts.length < 9 &&
            !!moreProductsWithoutVisibleProducts.length && (
              <div className="w-full">
                <div className="m-2 mt-4 border-t border-grey-200 pt-6 text-center text-xl font-black lg:mt-10 lg:pt-8">
                  <FormattedMessage id="cdp:similarproducts" />
                </div>
                <div
                  className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-5 "
                  data-test="collection-list"
                >
                  {moreProductsWithoutVisibleProducts.map(
                    (product, index: number) => (
                      <ProductImpressionsTracker
                        element="Product List"
                        key={`more-${product.id}-${product.promoteVariantId}`}
                        list={collection.title}
                        position={index}
                        product={product}
                        trackingGroupName={trackingListName || ''}
                        collectionHandle={trackingListName}
                      >
                        <div data-test="sometext3" className={'flex'}>
                          <ProductCard
                            product={product}
                            collectionTitle={collection.title}
                            position={index}
                            additionalQuery={additionalQuery}
                          />
                        </div>
                      </ProductImpressionsTracker>
                    )
                  )}
                </div>
              </div>
            )}
          {showMoreButton && limitOfProductsToShow && !customButton && (
            <ImpressionTrackerNew element={TRACKING_NAME}>
              <div
                className={cn(
                  'my-4 flex justify-center',
                  isMobile && 'container'
                )}
              >
                <Button
                  size="base"
                  className="w-1/2 lg:w-1/3"
                  layout="outline"
                  onClick={() => {
                    setLimitOfProductsToShow(undefined);
                    gtm(
                      selectElement({
                        element: TRACKING_NAME,
                      })
                    );
                  }}
                >
                  <FormattedMessage id="cdp:standalone-cdp:load-more" />
                </Button>
              </div>
            </ImpressionTrackerNew>
          )}
          {customButton}
        </div>
      </div>
    </>
  );
};

export default ProductsGrid;
